import React, { ReactElement } from "react";
import "@pds-react/tab/dist/tab.min.css"
import currency from "currency.js";
import { Condition } from "../../enums/Condition";
import _ from "lodash";
import { Relationship } from "../../enums/Relationship";
import LoadingSpinner from "../../atoms/LoadingSpinner";
import FlatTable from '../FlatTable';
import findConditionDescriptionFor = Condition.findConditionDescriptionFor;
import findConditionFromPlanTypeFor = Condition.findConditionFromPlanTypeFor;
import { useProduct } from "../../hooks/useProduct";
import { useCoverage } from "../../hooks/useCoverage";
import { Coverage } from "../../enums/Coverage";
import { BenefitsProps } from '../Benefits';
import BenefitsTab from "./BenefitsTab";
import { Provision } from "../../generated/graphql";

interface HospitalIndemnityBenefit {
    coverage: string
    condition: Condition
    amount: currency
    days: number,
    period: string,
    daySpan?: number,
    hideDays?: boolean
}

const HospitalIndemnityBenefits = (props: BenefitsProps) => {
    const coverage = useCoverage();
    const { loading: productLoading, data: product, error: productError } = useProduct()

    if(coverage !== Coverage.HOSPITAL_INDEMNITY) {
        return null;
    }

    let defaultCondition: Condition;
    const provisions = product?.configuration?.provisions;

    return (
        <>{buildBenefits()}</>
    )

    function buildBenefits() {
        if(productLoading) {
            return <LoadingSpinner/>
        } else if(productError || !product) {
            console.log('member group error', productError)
            return <p>Could not load member information, please try again later.</p>;
        }
        return <>
            {buildBenefitTabs()}
        </>
    }

    function buildBenefitTabs() {
        defaultCondition = findConditionFromPlanTypeFor(product.configuration.planType)

        const headers = ["Confinement benefits", "Additional confinement benefits", "Mental health and substance abuse benefits",
            "Outpatient benefits", "Emergency care benefits", "Surgical benefits", "Other benefits"]
        const relationships = Object.values(Relationship) as Relationship[];
        const sectionsByRelationship = new Map(relationships.map((r: Relationship) =>
            [
                r, [
                    buildConfinementBenefits(headers[0], r),
                    buildAdditionalConfinementBenefits(headers[1], r),
                    buildMentalHealthAndSubstanceAbuseBenefits(headers[2], r),
                    buildOutpatientBenefits(headers[3], r),
                    buildEmergencyCareBenefits(headers[4], r),
                    buildSurgicalBenefits(headers[5], r),
                    buildOtherBenefits(headers[6], r)
                ]
            ]
        ))
        return <BenefitsTab {...props} headers={headers} sectionsByRelationship={sectionsByRelationship} />;
    }

    function buildConfinementBenefits(header: string, relationship: Relationship) {
        const benefits = [
            ...buildSicknessAndInjuryBenefitsFor('First day hospital', provisions.firstDayHospitalSickness, provisions.firstDayHospitalInjury),
            ...buildSicknessAndInjuryBenefitsFor('First day hospital intensive care unit', provisions.firstDayIntensiveCareUnitSickness, provisions.firstDayIntensiveCareUnitInjury),
            ...buildSicknessAndInjuryBenefitsFor('Daily hospital confinement', provisions.dailyHospitalSickness, provisions.dailyHospitalInjury),
            ...buildSicknessAndInjuryBenefitsFor('Daily hospital intensive care unit confinement', provisions.dailyIntensiveCareUnitSickness, provisions.dailyIntensiveCareUnitInjury),
            buildBenefitFor('Newborn nursery confinement', defaultCondition, provisions.newbornNursery)
        ] as HospitalIndemnityBenefit[]
        return buildBenefitsTableFor(header, benefits, relationship);
    }

    function buildAdditionalConfinementBenefits(header: string, relationship: Relationship) {
        const benefits = [
            buildBenefitFor('Rehabilitation facility', defaultCondition, provisions.rehabilitationFacility),
            buildBenefitFor('Skilled nursing facility', defaultCondition, provisions.skilledNursingFacility),
            buildBenefitFor('Hospice care', defaultCondition, provisions.hospiceCare, 'included', 'amount', 'maximumDaysPerLifetime'),
        ] as HospitalIndemnityBenefit[]
        return buildBenefitsTableFor(header, benefits, relationship);
    }

    function buildMentalHealthAndSubstanceAbuseBenefits(header: string, relationship: Relationship) {
        const benefits = [
            buildBenefitFor('Mental disorder inpatient treatment facility', defaultCondition, provisions.mentalDisorderTreatmentFacility),
            buildBenefitFor('Substance abuse inpatient treatment facility', defaultCondition, provisions.substanceAbuseTreatmentFacility),
            buildBenefitFor('Mental disorder outpatient treatment', defaultCondition, provisions.mentalDisorderOutpatientTherapy),
            buildBenefitFor('Substance abuse outpatient treatment', defaultCondition, provisions.substanceAbuseOutpatientTherapy),
        ] as HospitalIndemnityBenefit[]
        return buildBenefitsTableFor(header, benefits, relationship);
    }

    function buildOutpatientBenefits(header: string, relationship: Relationship) {
        const benefits = [
            buildBenefitFor('Physician visit', defaultCondition, provisions.physicianVisit, 'inOfficeIncluded', 'inOfficeAmount', 'daysPerPersonPerYear'),
            buildBenefitFor('Telemedicine visit', defaultCondition, provisions.physicianVisit, 'telemedicineIncluded', 'telemedicineAmount', 'daysPerPersonPerYear'),
            buildBenefitFor('Lab test or X-ray', defaultCondition, provisions.labTestOrXray),
            buildBenefitFor('Major diagnostic procedure (diagnostic radiology)', defaultCondition, provisions.majorDiagnosticProcedure),
            buildBenefitFor('Invasive diagnostic procedure', defaultCondition, provisions.invasiveDiagnosticProcedure),
            buildBenefitFor('Prescription drug (brand name)', defaultCondition, provisions.prescriptionDrug, 'brandNameIncluded', 'brandNameAmount', 'daysPerYear', false, 2),
            buildBenefitFor('Prescription drug (generic)', defaultCondition, provisions.prescriptionDrug, 'genericIncluded', 'genericAmount', 'daysPerYear', true),
            buildBenefitFor('Durable medical equipment (tier 1)', defaultCondition, provisions.durableMedicalEquipment, 'tierOneIncluded', 'tierOneAmount', 'daysPerYear', false, 3),
            buildBenefitFor('Durable medical equipment (tier 2)', defaultCondition, provisions.durableMedicalEquipment, 'tierTwoIncluded', 'tierTwoAmount', 'daysPerYear', true),
            buildBenefitFor('Durable medical equipment (tier 3)', defaultCondition, provisions.durableMedicalEquipment, 'tierThreeIncluded', 'tierThreeAmount', 'daysPerYear', true),
            buildBenefitFor('Home health service', defaultCondition, provisions.homeHealthServices, 'included', 'amount', 'maximumDaysPerLifetime'),
            buildBenefitFor('Therapy services', defaultCondition, provisions.therapyServices),
            buildBenefitFor('Chiropractic care', defaultCondition, provisions.chiropracticCare),
        ] as HospitalIndemnityBenefit[]
        return buildBenefitsTableFor(header, benefits, relationship);
    }

    function buildEmergencyCareBenefits(header: string, relationship: Relationship) {
        const benefits = [
            buildBenefitFor('Air ambulance', defaultCondition, provisions.airAmbulance),
            buildBenefitFor('Ground or water ambulance', defaultCondition, provisions.groundOrWaterAmbulance),
            ...buildSicknessAndInjuryBenefitsFor('Emergency room', provisions.emergencyRoomSickness, provisions.emergencyRoomInjury),
            buildBenefitFor('Observation room', defaultCondition, provisions.observationUnit),
            buildBenefitFor('Urgent care facility', defaultCondition, provisions.urgentCareFacility),
        ] as HospitalIndemnityBenefit[]
        return buildBenefitsTableFor(header, benefits, relationship);
    }

    function buildSurgicalBenefits(header: string, relationship: Relationship) {
        const benefits = [
            buildBenefitFor('Inpatient surgery', defaultCondition, provisions.inpatientSurgery),
            buildBenefitFor('Outpatient surgery performed in a hospital or ambulatory surgical center', defaultCondition, provisions.outpatientSurgeryAmbulatory),
            buildBenefitFor('Outpatient surgery performed in a physician office', defaultCondition, provisions.outpatientSurgeryPhysiciansOffice),
            buildBenefitFor('General anesthesia', defaultCondition, provisions.generalAnesthesia),
            buildBenefitFor('Blood products', defaultCondition, provisions.bloodProducts),
            buildBenefitFor('Second opinion', defaultCondition, provisions.secondOpinion),
        ] as HospitalIndemnityBenefit[]
        return buildBenefitsTableFor(header, benefits, relationship);
    }

    function buildOtherBenefits(header: string, relationship: Relationship) {
        const benefits = [
            buildBenefitFor('Medical travel', defaultCondition, provisions.medicalTravel, 'included', 'amount', 'roundTripsPerYear'),
            buildBenefitFor('Companion lodging', defaultCondition, provisions.companionLodging),
            buildBenefitFor('Doula care', defaultCondition, provisions.doulaCare, 'included', 'amount', 'visitsPerPregnancy'),
            buildBenefitFor('Family care', defaultCondition, provisions.familyCare),
            buildBenefitFor('Pet care', defaultCondition, provisions.petCare),
        ] as HospitalIndemnityBenefit[]
        return buildBenefitsTableFor(header, benefits, relationship);
    }

    function buildBenefitsTableFor(title: string, benefits: HospitalIndemnityBenefit[], relationship: Relationship) {
        const truthyBenefits = benefits.filter(b => b);
        return <div>
            {!truthyBenefits.length
                ? `No ${title.toLowerCase()} found.`
                : <FlatTable title={title}
                             headers={['Coverage', 'Condition', 'Maximum amount', 'Maximum period'].map(h => ({ header: h }))}
                             rows={truthyBenefits
                                 .map(b => {
                                     const benefitColumns = [
                                         b.coverage,
                                         findConditionDescriptionFor(b.condition),
                                         multiplyAmountByRelationshipFor(b.amount, relationship).format(),
                                     ] as (string | ReactElement)[];
                                     if(!b.hideDays) {
                                         benefitColumns.push(
                                             <div {...(b.hideDays ? {} : {...{rowSpan: b.daySpan}})}>
                                                 {b.days?.toString()} {b.period ? _.startCase(b.period).toLowerCase().replace(/s\s/gi, '(s) ') : ''}
                                             </div>
                                         )
                                     }
                                     return benefitColumns
                                 })}/>
            }
        </div>;
    }

    function buildSicknessAndInjuryBenefitsFor(coverage: string, sicknessProvision?: Provision, injuryProvision?: Provision) {
        const sicknessBenefit = buildBenefitFor(coverage, Condition.SICKNESS, sicknessProvision);
        const injuryBenefit = buildBenefitFor(coverage, Condition.INJURY, injuryProvision);
        if(sicknessBenefit && injuryBenefit) {
            sicknessBenefit.hideDays = false
            sicknessBenefit.daySpan = 2
            injuryBenefit.hideDays = true
        }
        return [
            sicknessBenefit,
            injuryBenefit
        ]
    }

    function buildBenefitFor(coverage: string, condition: Condition, provision?: any,
                             includedField: string = 'included', amountField: string = 'amount',
                             daysField: string = 'daysPerYear', hideDays: boolean = false, daySpan: number = 1): HospitalIndemnityBenefit | undefined {
        if(!Object.values(Condition).includes(condition as Condition)
            || !provision || !provision[includedField] || !provision[amountField]) {
            return;
        }
        return {
            coverage,
            condition,
            amount: currency(provision[amountField]),
            days: provision[daysField],
            period: daysField,
            hideDays,
            daySpan
        };
    }

    function multiplyAmountByRelationshipFor(amount: currency, relationship: Relationship) {
        if([Relationship.DOMESTIC_PARTNER, Relationship.SPOUSE].includes(relationship)) {
            return amount.multiply(findFactorFor(product.configuration.spouseBenefitPercentageOfEmployee));
        } else if(relationship === Relationship.CHILD) {
            return amount.multiply(findFactorFor(product.configuration.childBenefitPercentageOfEmployee));
        }
        return amount;
    }

    function findFactorFor(benefit: number) {
        return benefit / 100
    }
}
export default HospitalIndemnityBenefits;
